// Courses API surface. Response unwrapping matches the planner convention:
// backend controllers wrap payloads as { resource } (models.Response.Data
// serialises under that key — see baseapi/models/response.go).
export { unwrap } from '@/pages/planner/api';

import type { User, WorkspaceGroup } from '@/lib/types';

// Assignees picked in the builder before the course is published — the
// assignment API refuses drafts (there is nothing for a learner to open yet),
// so the wizard stages targets and applies them the moment publish succeeds.
export interface StagedAssignees {
  users: User[];
  groups: WorkspaceGroup[];
}

export function emptyStagedAssignees(): StagedAssignees {
  return { users: [], groups: [] };
}

// Draft payload for creating/updating a course's own fields. Modules and
// items are managed through their own endpoints (slice 2).
export interface CourseDraft {
  id?: number;
  workspace_id: number;
  name: string;
  description: string;
  audience_type: string;
  estimated_minutes: number;
  cover_image: string;
  access_mode: 'login' | 'open';
  tracking_mode: 'individual' | 'count_only';
}

export function blankCourseDraft(workspaceId: number): CourseDraft {
  return {
    workspace_id: workspaceId,
    name: '',
    description: '',
    audience_type: '',
    estimated_minutes: 0,
    cover_image: '',
    access_mode: 'login',
    tracking_mode: 'individual',
  };
}

// Client-side working copy of one item row in the builder. `uid` is the
// drag-and-drop identity (stable across saves); `id` is the server row id —
// present means the row already exists and keeps its completion records
// through a reorder/relabel.
export interface CourseItemDraft {
  uid: string;
  id?: number;
  content_id: number | null;
  content_reference_id: string | null;
  content_type_id: number | null;
  content_type_name: string;
  content_name: string;
  source_label: string;
}

// Stable client-side id for drag-and-drop rows.
export function uid(): string {
  return crypto.randomUUID();
}

// Wire payload for the whole-ordered-replace items endpoint.
export function itemsPayload(items: CourseItemDraft[]) {
  return {
    items: items.map((item) => ({
      id: item.id ?? 0,
      content_id: item.content_id,
      content_reference_id: item.content_reference_id,
      content_type_id: item.content_type_id,
      content_type_name: item.content_type_name,
      content_name: item.content_name,
      source_label: item.source_label,
    })),
  };
}
